<template>
  <div class="fill-height">
    <NavigationToolbar :title="'Autorização de Saída'">
      <template slot="toolbar-action">
        <v-btn
          flat
          small
          color="white"
          :class="{ 'pulse-white': isAppOnline && !selectedClassroom && !updateExists }"
          @click="openDialog"
          :disabled="!isAppOnline"
          data-test="button-select-classroom">
          {{ selectedLabel }}
        </v-btn>
      </template>

      <OnlyOnline>
        <Spinner :loading="loading">
          <PageSelectClassroom
            v-if="!selectedClassroom"
            @openDialog="openDialog"
            key="select-classroom"
          />

          <div
            v-else-if="!emptyData"
            class="authorization-info"
            key="exit-authorized">
            <div class="authorization-info-content container pt-0 ">
              <CardAuthorization v-for="item in data" :key="item.id" :responsible="item"/>
            </div>
            <div class="authorization-info-action mt-2">
              <div class="container pt-0">
                <v-btn block @click="openConfirm" :disabled="!isAppOnline || noStudentsChecked" data-test="authorize-button">Liberar</v-btn>
              </div>
            </div>
          </div>

          <v-layout v-else key="no-results" pa-3 justify-center align-content-center fill-height column>
            <h2 class="text-xs-center font-weight-regular">
              No momento não há solicitações para serem autorizadas na sala {{ selectedClassroom.text }}.
            </h2>
          </v-layout>
        </Spinner>
      </OnlyOnline>
    </NavigationToolbar>

    <ClassroomSelection
      :dialog="dialogClassroomVisibility"
      @onSelectClassroom="onSelectClassroom"
      @cancelDialog="onCancelClassroomDialog"
      :selectedClassroom="selectedClassroom"
    />

    <DialogConfirm :visibility="dialogConfirmVisibility" :title="dialogConfirmTitle">
      <template slot="within-list">
        <template
          v-for="student in selectedStudents"
        >
          <StudentListConfirmation :student="student" :key="student.id"/>
        </template>
      </template>
      <template slot="left-side-action">
        <v-btn icon dark @click="hideDialogConfirmation">
          <v-icon>close</v-icon>
        </v-btn>
      </template>
      <template slot="bottom-action">
        <v-btn block @click="processDialogConfirmation" data-test="confirm-button" :disabled="!isAppOnline">Confirmar</v-btn>
      </template>
    </DialogConfirm>

  </div>
</template>

<script>
import CardAuthorization from '@/components/CardAuthorization'
import DialogConfirm from '@/components/DialogConfirm'
import NavigationToolbar from '@/layouts/NavigationToolbar'
import OnlyOnline from '@/components/OnlyOnline'
import PageSelectClassroom from './PageSelectClassroom'
import Spinner from '@/components/Spinner'
import StudentListConfirmation from './StudentListConfirmation'
import { user } from '@/services/'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  components: {
    ClassroomSelection: () => import('@/components/ClassroomSelection'),
    CardAuthorization,
    DialogConfirm,
    NavigationToolbar,
    OnlyOnline,
    PageSelectClassroom,
    Spinner,
    StudentListConfirmation
  },

  data: () => ({
    dialogClassroomVisibility: false,
    dialogConfirmTitle: 'Confirmação',
    dialogConfirmVisibility: false,
    selectedClassroom: null
  }),

  computed: {
    ...mapState({
      noStudentsChecked: state => (state.authorizationInfos.noStudentsChecked),
      data: state => (state.authorizationInfos.data),
      loadingActionCable: state => (state.socket.loadingActionCable),
      loadingClassroom: state => (state.authorizationInfos.loadingClassroom),
      loadingRequests: state => (state.authorizationInfos.loadingRequests),
      message: state => (state.authorizationInfos.message),
      isAppOnline: state => (state.network.online),
      updateExists: state => (state.swUpdate.updateExists)
    }),

    emptyData () {
      return this.data.length === 0
    },

    loading () {
      let isLoading = this.loadingRequests || this.loadingClassroom || this.loadingActionCable
      return isLoading
    },

    selectedLabel () {
      return this.selectedClassroom ? this.selectedClassroom.text : 'Selecionar Sala'
    },

    selectedStudents () {
      return this.getSelectedStudents()
    },

    requestData () {
      return this.getRequestData()
    }
  },

  watch: {
    selectedClassroom (newValue) {
      if (newValue) {
        const { text } = newValue

        if (text) {
          const param = user.getSelectedBuilding().value + '|' + text
          this.manageActionCable(param)
        } else {
          this.showSnackbar('Ocorreu um erro na tentativa de seleção da sala, por favor tente novamente.')
        }
      }
    },

    message (value) {
      if (value) {
        this.showSnackbar(value)
        this.wipeMessage()
      }
    }
  },

  async mounted () {
    this.fetchClassrooms()
    this.selectedClassroom = await this.getClassroom()
  },

  beforeDestroy () {
    this.unsubscribeActionCable()
  },

  methods: {
    ...mapActions({
      manageActionCable: 'manageActionCable',
      unsubscribeActionCable: 'unsubscribeActionCable',
      fetchClassrooms: 'authorizationInfos/fetchClassrooms',
      sendStudents: 'authorizationInfos/sendStudents',
      showSnackbar: 'snackbar/showSnack',
      wipeMessage: 'authorizationInfos/wipeMessage'
    }),

    ...mapGetters({
      getSelectedStudents: 'authorizationInfos/getSelectedStudents',
      getRequestData: 'authorizationInfos/getRequestData'
    }),

    getClassroom () {
      return user.getSelectedClassroom()
    },

    hideDialogConfirmation () {
      this.dialogConfirmVisibility = false
    },

    processDialogConfirmation () {
      this.hideDialogConfirmation()
      this.sendStudents(this.requestData)
    },

    async onSelectClassroom (value) {
      this.dialogClassroomVisibility = false
      await user.setSelectedClassroom(value)
      this.selectedClassroom = await this.getClassroom()
    },

    onCancelClassroomDialog () {
      this.dialogClassroomVisibility = false
    },

    openConfirm () {
      this.dialogConfirmVisibility = true
    },

    openDialog () {
      this.dialogClassroomVisibility = true
    }
  }
}
</script>

<style lang="stylus" scoped>
  .authorization-info
    height 100%
    display flex
    flex-direction column
    &-content
      display flex
      flex-direction column
      flex 1 0 0
      overflow-y scroll
    &-action
      height 60px
      overflow hidden
</style>
